import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

export default function Testimonials() {
  const { ref, isVisible } = useScrollAnimation<HTMLDivElement>();
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation<HTMLDivElement>();

  return (
    <section className="relative bg-[#0A0A0A] overflow-hidden">
      {/* Cinematic background */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: 'url(https://public.readdy.ai/ai/img_res/0d6bd354b0b3e27f895f4f244dfa0c62.jpg)' }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/97 via-[#0A0A0A]/85 to-[#0A0A0A]/70" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/50 via-transparent to-[#0A0A0A]/80" />

      {/* Top border accent */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#C9A030]/30 to-transparent" />

      {/* Main testimonial */}
      <div className="relative z-10 w-full px-6 lg:px-14 pt-24 pb-0">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-start">

            {/* Left: Label + credibility */}
            <div className="lg:col-span-2">
              <p className="overline mb-5">Client Results</p>
              <h2 className="font-serif text-4xl lg:text-[44px] font-light text-[#F2EDE3] leading-tight mb-8">
                Trusted by Energy Contractors
              </h2>
              <div className="space-y-5 mt-6">
                {[
                  { icon: 'ri-shield-check-fill', text: '100% Approval Guaranteed' },
                  { icon: 'ri-calendar-check-line', text: 'Month-to-Month Service' },
                  { icon: 'ri-user-star-line', text: 'Same Expert — Every Time' },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3">
                    <div className="w-8 h-8 border border-[#C9A030]/30 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-[#C9A030] text-sm`} />
                    </div>
                    <span className="text-[#9A9A9A] text-sm font-light">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Testimonial */}
            <div
              ref={ref}
              className={`lg:col-span-3 fade-left ${isVisible ? 'visible' : ''}`}
            >
              <div className="relative border-l-2 border-[#C9A030] pl-10 lg:pl-12">
                {/* Corner accent */}
                <div className="absolute top-0 left-0 w-0.5 h-12 bg-[#C9A030]" />

                {/* Large decorative quote */}
                <p className="font-serif text-[100px] lg:text-[120px] text-[#C9A030]/12 leading-none select-none -mb-6 -ml-3">
                  &ldquo;
                </p>

                <blockquote className="font-serif text-2xl lg:text-[30px] font-light text-[#F2EDE3] leading-[1.5] mb-10 italic">
                  ECS helps me with our safety training and keeping our ISNetworld scores high. It helps to get things done.
                </blockquote>

                <div className="flex items-center gap-5">
                  <div className="w-12 h-12 bg-[#C9A030]/15 border border-[#C9A030]/30 flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-line text-[#C9A030] text-lg" />
                  </div>
                  <div>
                    <p className="text-[#F2EDE3] font-semibold text-base tracking-wide">Milt Chappel</p>
                    <p className="text-[#C9A030] text-xs tracking-[0.2em] uppercase font-medium mt-1">
                      Dustin Donley Construction Services LLC
                    </p>
                    <div className="flex items-center gap-1 mt-2">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-star-fill text-[#C9A030] text-xs" />
                        </span>
                      ))}
                      <span className="text-[#9A9A9A] text-[10px] ml-1 tracking-wide">ISNetworld Client</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Embedded mid-page CTA */}
      <div className="relative z-10 w-full px-6 lg:px-14 py-16">
        <div
          ref={ctaRef}
          className={`max-w-6xl mx-auto flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 pt-10 border-t border-[#C9A030]/12 fade-up ${ctaVisible ? 'visible' : ''}`}
        >
          <div>
            <p className="font-serif text-2xl lg:text-3xl font-light text-[#F2EDE3] leading-snug mb-2">
              Ready to achieve 100% compliance?
            </p>
            <p className="text-[#9A9A9A] text-sm font-light">No contracts. Same expert. Guaranteed results.</p>
          </div>
          <div className="flex flex-wrap gap-3 flex-shrink-0">
            <Link
              to="/contact"
              className="bg-[#C9A030] text-[#0A0A0A] px-7 py-3.5 text-[11px] font-bold tracking-[0.2em] uppercase whitespace-nowrap cursor-pointer transition-colors duration-200 hover:bg-[#D4B045]"
            >
              Start Today
            </Link>
            <a
              href="tel:4054081117"
              className="border border-[#F2EDE3]/18 text-[#F2EDE3]/60 px-7 py-3.5 text-[11px] font-medium tracking-[0.2em] uppercase whitespace-nowrap cursor-pointer transition-colors duration-200 hover:border-[#C9A030]/50 hover:text-[#C9A030]"
            >
              Call 405-408-1117
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
